import { graphql, useFragment } from 'react-relay';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';

import { useShippingType_viewer$key } from './__generated__/useShippingType_viewer.graphql';
import { useShippingType_item$key } from './__generated__/useShippingType_item.graphql';

const viewerFragment = graphql`
    fragment useShippingType_viewer on Viewer
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        itemId: { type: "String", defaultValue: "" }
        fetchRegionalInfo: { type: "Boolean", defaultValue: false }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
        itemQuantity: { type: "Int" }
        fdfQuoteABTestVariant: { type: "String" }
    ) {
        relevantShipping(
            itemId: $itemId
            buyerId: $userId
            quantity: $itemQuantity
            zipCode: $userZipCode
            countryCode: $userCountryCode
            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
        ) {
            shipmentQuote {
                subTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
        }
        ...useShippingPrice_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

const itemFragment = graphql`
    fragment useShippingType_item on Item
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        itemQuantity: { type: "Int" }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
        fdfQuoteABTestVariant: { type: "String" }
    ) {
        ...useShippingPrice_item
            @arguments(
                buyerId: $userId
                fetchShippingCostInfo: true
                itemQuantity: $itemQuantity
                fdfQuoteABTestVariant: $fdfQuoteABTestVariant
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

const SHIPPING_TYPE = {
    PAID: 'paid',
    PAID_WITH_DISCOUNT: 'paid_discounted',
    FREE_SHIPPING: 'free',
    FREE_SHIPPING_WITH_DISCOUNT: 'free_discounted',
} as const;

export type ShippingType = (typeof SHIPPING_TYPE)[keyof typeof SHIPPING_TYPE];

const trackingMap: Record<ShippingType | 'shipping_promo', string> = {
    [SHIPPING_TYPE.PAID]: 'not free, no quote',
    [SHIPPING_TYPE.PAID_WITH_DISCOUNT]: 'not free, no quote',
    [SHIPPING_TYPE.FREE_SHIPPING]: 'free shipping',
    [SHIPPING_TYPE.FREE_SHIPPING_WITH_DISCOUNT]: 'free subsidized shipping',
    shipping_promo: 'shipping promo',
};
export const getShippingTypeTracking = (type: keyof typeof trackingMap): string =>
    trackingMap[type] || 'not free, no quote';

export const useShippingType = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
}: {
    viewer: useShippingType_viewer$key | null;
    item: useShippingType_item$key | null;
    currency: string;
}): ShippingType => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const price = useShippingPrice({
        viewer,
        item,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });
    const fullPrice =
        viewer?.relevantShipping?.shipmentQuote?.subTotalAmount?.convertedAmountList?.find(
            p => p?.currency === currency
        )?.amount || 0;

    const isFreeShipping = price === 0;
    const isDiscountedShipping =
        (!!price || isFreeShipping) && fullPrice !== 0 && fullPrice > price;

    if (isFreeShipping && isDiscountedShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING_WITH_DISCOUNT;
    } else if (isFreeShipping) {
        return SHIPPING_TYPE.FREE_SHIPPING;
    } else if (isDiscountedShipping) {
        return SHIPPING_TYPE.PAID_WITH_DISCOUNT;
    }

    return SHIPPING_TYPE.PAID;
};
